import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Overdue Reports Dashboard
 *
 * Displays three key reports:
 * 1. Overdue A/R (Accounts Receivable) - Unpaid customer invoices
 * 2. Overdue A/P (Accounts Payable) - Unpaid supplier purchases
 * 3. Unreturned Certificates - Diamonds with certificates out on memo
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../utils/apiClient';
import { notify } from '../../utils/notifications';
import mockDataService from '../../services/mockDataService';
import { FaFileInvoiceDollar, FaShoppingCart, FaCertificate, FaDownload, FaExclamationTriangle } from 'react-icons/fa';
import { PageContainer, PageTitle, SectionTitle, ContentSection, Text, Button } from '../../components/PageStyles';
// Stats Grid
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;
const StatCard = styled.div `
  background: var(--bg-secondary);
  padding: 24px;
  border: 1px solid var(--border-color);
  text-align: center;

  .stat-icon {
    font-size: 40px;
    color: var(--text-tertiary);
    margin-bottom: 16px;
  }

  .stat-value {
    font-size: 32px;
    color: var(--text-primary);
    font-weight: bold;
    margin-bottom: 8px;
  }

  .stat-label {
    font-size: 14px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 4px;
  }

  .stat-sublabel {
    font-size: 12px;
    color: var(--text-tertiary);
    margin-top: 8px;
  }
`;
// Tab Navigation
const TabContainer = styled.div `
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 32px;
`;
const TabList = styled.div `
  display: flex;
  gap: 4px;
`;
const Tab = styled.button `
  background: ${props => props.$isActive ? 'var(--bg-secondary)' : 'transparent'};
  color: ${props => props.$isActive ? 'var(--text-primary)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$isActive ? 'var(--border-color)' : 'transparent'};
  border-bottom: ${props => props.$isActive ? 'none' : '1px solid var(--border-color)'};
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  top: 1px;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }

  .badge {
    background: #ef4444;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    margin-left: 8px;
    font-weight: bold;
  }
`;
// Table Styles
const Table = styled.table `
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);

  thead {
    background: var(--bg-tertiary);
    border-bottom: 2px solid var(--border-color);

    th {
      padding: 12px 16px;
      text-align: left;
      font-size: 12px;
      color: var(--text-tertiary);
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 700;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid var(--border-color);
      transition: background 0.2s;

      &:hover {
        background: var(--bg-tertiary);
      }

      &:last-child {
        border-bottom: none;
      }

      td {
        padding: 16px;
        font-size: 14px;
        color: var(--text-secondary);

        &.highlight {
          color: var(--text-primary);
          font-weight: 600;
        }
      }
    }
  }
`;
const MockModeToggle = styled.button `
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.active ? 'rgba(16, 185, 129, 0.15)' : 'var(--bg-tertiary)'};
  color: ${props => props.active ? '#10b981' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.active ? '#10b981' : 'var(--border-color)'};
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: ${props => props.active ? 'rgba(16, 185, 129, 0.25)' : 'var(--bg-secondary)'};
    border-color: ${props => props.active ? '#10b981' : 'var(--border-color-hover)'};
  }
`;
const OverdueReports = () => {
    const [activeTab, setActiveTab] = useState('ar');
    const [isLoading, setIsLoading] = useState(true);
    const [isMockMode, setIsMockMode] = useState(mockDataService.isMockMode());
    // Data states
    const [arData, setArData] = useState(null);
    const [apData, setApData] = useState(null);
    const [certificatesData, setCertificatesData] = useState(null);
    // Fetch all reports on mount
    useEffect(() => {
        fetchAllReports();
    }, []);
    const handleToggleMockMode = () => {
        const newMode = mockDataService.toggleMockMode();
        setIsMockMode(newMode);
        window.location.reload();
    };
    const fetchAllReports = async () => {
        setIsLoading(true);
        try {
            if (isMockMode) {
                // Use mock data
                const invoices = mockDataService.getInvoices();
                const purchases = mockDataService.getPurchases();
                // Filter overdue invoices
                const now = new Date();
                const overdueInvoices = invoices.filter(inv => (inv.status === 'issued' || inv.status === 'overdue') &&
                    new Date(inv.dueDate) < now).map(inv => ({
                    ...inv,
                    daysOverdue: Math.floor((now.getTime() - new Date(inv.dueDate).getTime()) / (1000 * 60 * 60 * 24)),
                    amountDue: inv.total - (inv.amountPaid || 0)
                }));
                const totalOverdueAmount = overdueInvoices.reduce((sum, inv) => sum + inv.amountDue, 0);
                setArData({
                    invoices: overdueInvoices,
                    summary: {
                        count: overdueInvoices.length,
                        totalOverdueAmount,
                        avgDaysOverdue: overdueInvoices.length > 0
                            ? Math.round(overdueInvoices.reduce((sum, inv) => sum + inv.daysOverdue, 0) / overdueInvoices.length)
                            : 0
                    }
                });
                // Filter overdue purchases
                const overduePurchases = purchases.filter(p => (p.paymentStatus === 'unpaid' || p.paymentStatus === 'partial') &&
                    p.dueDate &&
                    new Date(p.dueDate) < now).map(p => ({
                    ...p,
                    daysOverdue: p.dueDate ? Math.floor((now.getTime() - new Date(p.dueDate).getTime()) / (1000 * 60 * 60 * 24)) : 0
                }));
                const totalOwed = overduePurchases.reduce((sum, p) => sum + p.amountOwed, 0);
                setApData({
                    purchases: overduePurchases,
                    summary: {
                        count: overduePurchases.length,
                        totalOwed,
                        avgDaysOverdue: overduePurchases.length > 0
                            ? Math.round(overduePurchases.reduce((sum, p) => sum + p.daysOverdue, 0) / overduePurchases.length)
                            : 0
                    }
                });
                // Filter memos with certificates that are shipped or with client
                const memos = mockDataService.getMemos();
                const memosWithCertificates = memos.filter(m => (m.status === 'shipped' || m.status === 'with_client') &&
                    m.diamondDetails.certificateNumber).map(m => {
                    let daysOut = null;
                    let isOverdue = false;
                    if (m.dueDate) {
                        const dueDate = new Date(m.dueDate);
                        const diffTime = now.getTime() - dueDate.getTime();
                        daysOut = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                        isOverdue = daysOut > 0;
                    }
                    return {
                        id: m.id,
                        memoId: m.id,
                        dealerName: m.dealerName,
                        dealerEmail: m.dealerEmail,
                        dealerBusinessName: m.dealerBusinessName,
                        clientName: m.clientName,
                        status: m.status,
                        requestedAt: m.requestedAt,
                        shippedAt: m.shippedAt,
                        dueDate: m.dueDate,
                        daysOut,
                        isOverdue,
                        diamond: {
                            shape: m.diamondDetails.shape,
                            caratWeight: m.diamondDetails.caratWeight,
                            color: m.diamondDetails.color,
                            clarity: m.diamondDetails.clarity,
                            cut: m.diamondDetails.cut,
                            lab: m.diamondDetails.lab,
                            certificateNumber: m.diamondDetails.certificateNumber,
                            stockNumber: m.diamondDetails.stockNumber,
                            priceUSD: m.diamondDetails.priceUSD
                        }
                    };
                });
                const overdueCount = memosWithCertificates.filter(m => m.isOverdue).length;
                const totalValue = memosWithCertificates.reduce((sum, m) => sum + (m.diamond.priceUSD || 0), 0);
                setCertificatesData({
                    memos: memosWithCertificates,
                    summary: {
                        count: memosWithCertificates.length,
                        overdueCount,
                        totalValue
                    }
                });
            }
            else {
                // Use real API
                const [arResponse, apResponse, certResponse] = await Promise.all([
                    apiClient.get('reports/overdue-ar'),
                    apiClient.get('reports/overdue-ap'),
                    apiClient.get('reports/unreturned-certificates')
                ]);
                if (arResponse.ok && arResponse.data?.success) {
                    setArData(arResponse.data.data);
                }
                else {
                    console.error('Failed to load A/R report:', arResponse.error);
                }
                if (apResponse.ok && apResponse.data?.success) {
                    setApData(apResponse.data.data);
                }
                else {
                    console.error('Failed to load A/P report:', apResponse.error);
                }
                if (certResponse.ok && certResponse.data?.success) {
                    setCertificatesData(certResponse.data.data);
                }
                else {
                    console.error('Failed to load certificates report:', certResponse.error);
                }
            }
        }
        catch (error) {
            console.error('Error fetching reports:', error);
            notify.error('Load Failed', 'Failed to load overdue reports');
        }
        finally {
            setIsLoading(false);
        }
    };
    const exportToCSV = (data, filename) => {
        if (data.length === 0) {
            notify.warning('No Data', 'No data to export');
            return;
        }
        // Convert to CSV
        const headers = Object.keys(data[0]).join(',');
        const rows = data.map(row => Object.values(row).join(','));
        const csv = [headers, ...rows].join('\n');
        // Download
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        notify.success('Export Complete', `${filename} downloaded successfully`);
    };
    if (isLoading) {
        return (_jsxs(PageContainer, { children: [_jsx(PageTitle, { children: "Overdue Reports" }), _jsx(Text, { children: "Loading reports..." })] }));
    }
    const totalAROverdue = arData?.summary?.totalOverdueAmount || 0;
    const totalAPOwed = apData?.summary?.totalOwed || 0;
    const totalCertificates = certificatesData?.summary?.count || 0;
    return (_jsxs("div", { style: { padding: '0' }, children: [_jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsx("div", { className: "stat-icon", children: _jsx(FaFileInvoiceDollar, {}) }), _jsx("div", { className: "stat-label", children: "Overdue A/R" }), _jsxs("div", { className: "stat-value", children: ["$", totalAROverdue.toLocaleString()] }), _jsxs("div", { className: "stat-sublabel", children: [arData?.summary?.count || 0, " overdue invoices"] })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-icon", children: _jsx(FaShoppingCart, {}) }), _jsx("div", { className: "stat-label", children: "Overdue A/P" }), _jsxs("div", { className: "stat-value", children: ["$", totalAPOwed.toLocaleString()] }), _jsxs("div", { className: "stat-sublabel", children: [apData?.summary?.count || 0, " unpaid purchases"] })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-icon", children: _jsx(FaCertificate, {}) }), _jsx("div", { className: "stat-label", children: "Unreturned Certificates" }), _jsx("div", { className: "stat-value", children: totalCertificates }), _jsxs("div", { className: "stat-sublabel", children: [certificatesData?.summary?.overdueCount || 0, " overdue"] })] })] }), _jsx(TabContainer, { children: _jsxs(TabList, { children: [_jsxs(Tab, { "$isActive": activeTab === 'ar', onClick: () => setActiveTab('ar'), children: ["Overdue A/R", arData?.summary?.count > 0 && (_jsx("span", { className: "badge", children: arData.summary.count }))] }), _jsxs(Tab, { "$isActive": activeTab === 'ap', onClick: () => setActiveTab('ap'), children: ["Overdue A/P", apData?.summary?.count > 0 && (_jsx("span", { className: "badge", children: apData.summary.count }))] }), _jsxs(Tab, { "$isActive": activeTab === 'certificates', onClick: () => setActiveTab('certificates'), children: ["Unreturned Certificates", certificatesData?.summary?.overdueCount > 0 && (_jsx("span", { className: "badge", children: certificatesData.summary.overdueCount }))] })] }) }), _jsxs(ContentSection, { children: [activeTab === 'ar' && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }, children: [_jsx(SectionTitle, { children: "Overdue Accounts Receivable" }), _jsxs(Button, { variant: "secondary", onClick: () => exportToCSV(arData?.invoices || [], 'overdue-ar.csv'), children: [_jsx(FaDownload, { style: { marginRight: '8px' } }), "Export to CSV"] })] }), arData?.invoices && arData.invoices.length > 0 ? (_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Invoice #" }), _jsx("th", { children: "Customer" }), _jsx("th", { children: "Due Date" }), _jsx("th", { children: "Days Overdue" }), _jsx("th", { children: "Amount Due" })] }) }), _jsx("tbody", { children: arData.invoices.map((invoice) => (_jsxs("tr", { children: [_jsx("td", { className: "highlight", children: invoice.invoiceNumber }), _jsx("td", { children: invoice.customerName }), _jsx("td", { children: new Date(invoice.dueDate).toLocaleDateString() }), _jsx("td", { children: _jsxs("span", { style: { color: invoice.daysOverdue > 60 ? '#ef4444' : '#f59e0b', fontWeight: 'bold' }, children: [invoice.daysOverdue, " days"] }) }), _jsxs("td", { className: "highlight", children: ["$", invoice.amountDue.toLocaleString()] })] }, invoice.id))) })] })) : (_jsx(Text, { style: { textAlign: 'center', padding: '48px', color: 'var(--text-tertiary)' }, children: "No overdue invoices" }))] })), activeTab === 'ap' && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }, children: [_jsx(SectionTitle, { children: "Overdue Accounts Payable" }), _jsxs(Button, { variant: "secondary", onClick: () => exportToCSV(apData?.purchases || [], 'overdue-ap.csv'), children: [_jsx(FaDownload, { style: { marginRight: '8px' } }), "Export to CSV"] })] }), apData?.purchases && apData.purchases.length > 0 ? (_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Purchase #" }), _jsx("th", { children: "Supplier" }), _jsx("th", { children: "Due Date" }), _jsx("th", { children: "Days Overdue" }), _jsx("th", { children: "Amount Owed" })] }) }), _jsx("tbody", { children: apData.purchases.map((purchase) => (_jsxs("tr", { children: [_jsx("td", { className: "highlight", children: purchase.purchaseNumber }), _jsx("td", { children: purchase.supplierName }), _jsx("td", { children: new Date(purchase.dueDate).toLocaleDateString() }), _jsx("td", { children: _jsxs("span", { style: { color: purchase.daysOverdue > 60 ? '#ef4444' : '#f59e0b', fontWeight: 'bold' }, children: [purchase.daysOverdue, " days"] }) }), _jsxs("td", { className: "highlight", children: ["$", purchase.amountOwed.toLocaleString()] })] }, purchase.id))) })] })) : (_jsx(Text, { style: { textAlign: 'center', padding: '48px', color: 'var(--text-tertiary)' }, children: "No overdue purchases" }))] })), activeTab === 'certificates' && (_jsxs(_Fragment, { children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }, children: [_jsx(SectionTitle, { children: "Unreturned Certificates" }), _jsxs(Button, { variant: "secondary", onClick: () => exportToCSV(certificatesData?.memos || [], 'unreturned-certificates.csv'), children: [_jsx(FaDownload, { style: { marginRight: '8px' } }), "Export to CSV"] })] }), certificatesData?.memos && certificatesData.memos.length > 0 ? (_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Certificate #" }), _jsx("th", { children: "Dealer" }), _jsx("th", { children: "Diamond" }), _jsx("th", { children: "Due Date" }), _jsx("th", { children: "Status" })] }) }), _jsx("tbody", { children: certificatesData.memos.map((memo) => (_jsxs("tr", { children: [_jsx("td", { className: "highlight", children: memo.diamond.certificateNumber }), _jsx("td", { children: memo.dealerName }), _jsxs("td", { children: [memo.diamond.caratWeight, "ct ", memo.diamond.shape, " ", memo.diamond.color, " ", memo.diamond.clarity] }), _jsx("td", { children: memo.dueDate ? new Date(memo.dueDate).toLocaleDateString() : 'N/A' }), _jsx("td", { children: memo.isOverdue ? (_jsxs("span", { style: { color: '#ef4444', fontWeight: 'bold' }, children: [_jsx(FaExclamationTriangle, { style: { marginRight: '4px' } }), "Overdue (", memo.daysOut, " days)"] })) : (_jsx("span", { style: { color: '#10b981' }, children: "Active" })) })] }, memo.id))) })] })) : (_jsx(Text, { style: { textAlign: 'center', padding: '48px', color: 'var(--text-tertiary)' }, children: "No unreturned certificates" }))] }))] })] }));
};
export default OverdueReports;
